import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { FaUserPlus, FaLock } from "react-icons/fa";

/**
 * @file Signup.js
 * @description Signup page for new super admin accounts.
 */

export default function Signup() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        username: "",
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        full_name: ""
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const upd = { ...prev, [name]: value };
            if (name === "first_name" || name === "last_name") {
                upd.full_name = `${upd.first_name} ${upd.last_name}`.trim();
            }
            return upd;
        });
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setError("");

        // Basic validation
        if (!formData.username || !formData.email || !formData.password || !formData.first_name) {
            setError("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        try {
            await authService.signup(formData);
            setSuccess(true);
            // Optional: Redirect after a delay
            setTimeout(() => {
                navigate("/login", { state: { message: "Account created! Please log in." } });
            }, 2000);
        } catch (err) {
            setError(err.message || "Signup failed. Please try again.");
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light">
                <div className="card border-0 shadow-lg p-5 text-center" style={{ maxWidth: "400px" }}>
                    <div className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-4 mx-auto" style={{ width: 80, height: 80 }}>
                        <FaUserPlus size={40} />
                    </div>
                    <h3 className="fw-bold text-success mb-3">Account Created!</h3>
                    <p className="text-muted mb-4">You have successfully signed up. Redirecting to login...</p>
                    <Link to="/login" className="btn btn-primary w-100">Go to Login</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light">
            <div className="card border-0 shadow-lg p-4" style={{ maxWidth: "500px", width: "100%" }}>
                <div className="text-center mb-4">
                    <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 60, height: 60 }}>
                        <FaUserPlus size={24} />
                    </div>
                    <h4 className="fw-bold">Create Account</h4>
                    <p className="text-muted small">Sign up for the Super Admin Console</p>
                </div>

                <form onSubmit={handleSignup}>
                    {error && <div className="alert alert-danger py-2 small">{error}</div>}

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold text-secondary">First Name *</label>
                            <input type="text" className="form-control" name="first_name" value={formData.first_name} onChange={handleChange} required />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold text-secondary">Last Name</label>
                            <input type="text" className="form-control" name="last_name" value={formData.last_name} onChange={handleChange} />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label small fw-bold text-secondary">Username *</label>
                        <input type="text" className="form-control" name="username" value={formData.username} onChange={handleChange} required />
                    </div>

                    <div className="mb-3">
                        <label className="form-label small fw-bold text-secondary">Email Address *</label>
                        <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} required />
                    </div>

                    <div className="mb-4">
                        <label className="form-label small fw-bold text-secondary">Password *</label>
                        <input type="password" className="form-control" name="password" value={formData.password} onChange={handleChange} required />
                    </div>

                    <button type="submit" className="btn btn-primary w-100 py-2 fw-bold" disabled={loading}>
                        {loading ? "Creating Account..." : "Sign Up"}
                    </button>
                </form>

                <div className="text-center mt-4 pt-3 border-top">
                    <small className="text-muted">Already have an account? <Link to="/login" className="fw-bold text-primary text-decoration-none">Sign In</Link></small>
                </div>
            </div>
        </div>
    );
}
